'use client'

import { useState, useEffect, useCallback } from 'react'
import { createClient } from '@/utils/supabase/client'
import { Tables } from '@/utils/schema'

type Notification = Tables<'notifications'>

export function useNotifications(userId?: string) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [alertsUnreadCount, setAlertsUnreadCount] = useState(0)
  const supabase = createClient()

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setNotifications([])
      setUnreadCount(0)
      setAlertsUnreadCount(0)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      let workspaceId = readCurrentWorkspaceId()
      if (!workspaceId) {
        const { data: fallbackWorkspace } = await supabase
          .from('workspace_members')
          .select('workspace_id')
          .eq('auth_user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()

        workspaceId = (fallbackWorkspace?.workspace_id as string | undefined) ?? null
      }

      const notificationsQuery = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50)
      const nowIso = new Date().toISOString()
      const alertsQuery = workspaceId
        ? supabase
            .from('activity_events' as any)
            .select('id', { count: 'exact', head: true })
            .eq('workspace_id', workspaceId)
            .eq('is_read', false)
            .is('resolved_at', null)
            .is('dismissed_at', null)
            .or(`snoozed_until.is.null,snoozed_until.lt.${nowIso}`)
        : Promise.resolve({ count: 0, error: null })

      const [{ data, error }, { count: alertsCount, error: alertsError }] = await Promise.all([
        notificationsQuery,
        alertsQuery,
      ])

      if (error) {
        console.error('Error fetching notifications:', error)
      } else {
        const notifications = (data || []) as Notification[]
        setNotifications(notifications)
        setUnreadCount(notifications.filter(n => !n.read).length)
      }

      if (alertsError) {
        console.error('Error fetching alerts unread count:', alertsError)
      } else {
        setAlertsUnreadCount(alertsCount || 0)
      }
    } catch (error) {
      console.error('Error in fetchNotifications:', error)
    } finally {
      setIsLoading(false)
    }
  }, [userId, supabase])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)

      if (error) {
        console.error('Error marking notification as read:', error)
        return
      }

      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error in markAsRead:', error)
    }
  }, [supabase])

  const markAllAsRead = useCallback(async () => {
    if (!userId) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false)

      if (error) {
        console.error('Error marking all notifications as read:', error)
        return
      }

      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      )
      setUnreadCount(0)
    } catch (error) {
      console.error('Error in markAllAsRead:', error)
    }
  }, [userId, supabase])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  useEffect(() => {
    const handleDeleted = (event: Event) => {
      const detail = (event as CustomEvent<{ unread?: boolean }>).detail
      if (!detail?.unread) return
      setAlertsUnreadCount((prev) => Math.max(0, prev - 1))
    }

    window.addEventListener('alerts:deleted', handleDeleted)
    return () => window.removeEventListener('alerts:deleted', handleDeleted)
  }, [])

  return {
    notifications,
    isLoading,
    unreadCount,
    alertsUnreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  }
}

function readCurrentWorkspaceId(): string | null {
  if (typeof document === 'undefined') return null

  const cookieMatch = document.cookie.match(/(?:^|;\s*)currentWorkspaceId=([^;]+)/)
  if (cookieMatch?.[1]) return decodeURIComponent(cookieMatch[1])

  try {
    return window.localStorage.getItem('currentWorkspaceId')
  } catch {
    return null
  }
}
